import { ProductAndCoveragesResponse, ProductPricingResponse } from '@apis/models';
import { ZestyPlansAndPricesCoverage } from '@apis/models/zesty.api.model';
import { CoverageDependencyHandler } from '@apis/utils/coverage-dependencies';
import ZestyApi from '@apis/zesty.apis';
import { IsTheme, Theme } from '@app/core/featureToggle';
import { FormNewOrderCoverageTablesProps } from '@components/form/FormNewOrderCoverageTables';
import REText from '@components/wrappedBDS/REText';
import { coverageNameOverridesDictionary } from '@constants/dictionaries';
import { Panel } from '@ftdr/blueprint-components-react';
import { ProductAddOnSelectionListMicroFrontend } from '@ftdr/product-add-on-selection-list-micro-frontend';
import { getBrand } from '@helpers/brand.utils';
import { isProdEnv } from '@helpers/utils';
import { PLANS_AND_PRICES_LABELS } from '@templates/misc/PlansAndPricesTemplate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

export const GUEST_UNIT_LABEL = coverageNameOverridesDictionary['Guest Unit'];

import { Tenant } from '@ftdr/product-comparison-cards-micro-frontend';
import { getCurrentThemeConfig } from '@app/core/theme-config';

const CoverageAddonsMFE: React.FC<FormNewOrderCoverageTablesProps> = ({
  availablePlansAndCoverages,
  planPricingData,
  selectedPlanCoverages,
  selectedGuestUnit,
  onSelectGuestUnit,
  onSelectOptionalCoverage,
  onSelectGroupCoverage,
  onQuantityChangeOptionalCoverage,
  onMFELoadIndicator,
}) => {
  const [zestyCoverageMeta, setZestyCoverageMeta] = useState<ZestyPlansAndPricesCoverage[]>([]);
  const [isLoadingInitialSelection, setIsLoadingInitialSelection] = useState(true);
  const coverageHandler = new CoverageDependencyHandler();

  const zestyUrl = isProdEnv()
  ? 'https://productcoverage.zestyapi.frontdoorhome.com'
  : 'https://productcoverage-dev.zesty.dev';

  const selectGuestCoverage = (cvg) => {
    onSelectGuestUnit();
    onSelectOptionalCoverage(cvg);
  };

  const loadZestyCoverageMeta = () => {
    ZestyApi.GetPlansAndPricesCoverages(getBrand())
      .then((data) => {
        setZestyCoverageMeta(data);
      })
      .catch((e) => {
        console.error('failed to load zesty coverage meta', e);
      });
  };

  /** get the data related to pricing from selection.  Returns null if data not populated or no product selected.  */
  const priceData: ProductPricingResponse = useMemo(() => {
    if (!selectedPlanCoverages?.product) {
      return null;
    }
    return planPricingData?.find(
      (data) => data?.product?.id === selectedPlanCoverages.product.starPVID,
    );
  }, [selectedPlanCoverages, planPricingData]);

  /** get the data related to planAndCoverages from selection. Returns null if data not populated or no product selected. */
  const planAndCoverages: ProductAndCoveragesResponse = useMemo(() => {
    if (!selectedPlanCoverages?.product) {
      return null;
    }
    return availablePlansAndCoverages?.find(
      (plan) => plan?.product?.id === selectedPlanCoverages.product.starPVID,
    );
  }, [selectedPlanCoverages, availablePlansAndCoverages]);

  const getCoverageItem = (
    type: 'optional' | 'group' | 'oldProductGuestUnit',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any,
  ) => {
    const { cvg, cvgPriceData, selectedGuestUnit, priceData } = params;
    return {
      coverageId: type === 'optional' ? (cvg?.starCoverageId ?? null) : null,
      label: type === 'oldProductGuestUnit' ? GUEST_UNIT_LABEL : cvg.name,
      price:
        type === 'oldProductGuestUnit'
          ? (priceData?.guestUnitPrice ?? 0)
          : (selectedGuestUnit ? cvgPriceData?.priceWithGuest : cvgPriceData?.price) || 0,
      groupId: type === 'group' ? (cvg?.starGroupId ?? null) : null,
      onSelect:
        type === 'oldProductGuestUnit'
          ? () => onSelectGuestUnit()
          : type === 'optional'
            ? () =>
                cvg?.name === GUEST_UNIT_LABEL
                  ? selectGuestCoverage(cvg)
                  : onSelectOptionalCoverage(cvg)
            : () => onSelectGroupCoverage(cvg),
      onQuantityChange:
        type === 'optional'
          ? (_, qty) => onQuantityChangeOptionalCoverage(cvg.starCoverageId, qty)
          : null,
      quantityEnabled: type === 'optional',
      meta: { cvgType: type },
    };
  };

  /** transforms optional coverages into coverage table items based on props */
  const optionalCoverageTableItems = useMemo(() => {
    if (!planAndCoverages || !priceData) {
      return [];
    }

    const availableCoverages =
      zestyCoverageMeta?.length > 0
        ? planAndCoverages.coverages.optional.filter((cv) => {
            return zestyCoverageMeta.some((zestyData) => {
              return zestyData.coverage_id === cv.starCoverageId && zestyData.ishidden === '0';
            });
          })
        : planAndCoverages.coverages.optional;

    // add optional coverages
    return availableCoverages.map((cvg) =>
      getCoverageItem('optional', {
        cvg,
        cvgPriceData: priceData.optionalCoverages.find(
          (cvgPrice) => cvg.starCoverageId === cvgPrice.id,
        ),
        selectedGuestUnit,
        selectGuestCoverage,
      }),
    );
  }, [planAndCoverages, priceData, selectedPlanCoverages, selectedGuestUnit, zestyCoverageMeta]);

  /** transforms guest unit into coverage table items based on props.
   * NOTE: THIS IS FOR OLD RE1 PRODUCTS.  We want to remove this once we do not support old RE1 products anymore. */
  const oldProductGuestUnitTableItems = useMemo(() => {
    if (!priceData || priceData.guestUnitPrice === 0) return [];
    return [
      getCoverageItem('oldProductGuestUnit', {
        priceData,
        selectedGuestUnit,
      }),
    ];
  }, [priceData, selectedGuestUnit]);

  const groupCoverageTableItems = useMemo(() => {
    if (!planAndCoverages || !priceData) return [];
    return planAndCoverages.coverages.group.map((cvg) =>
      getCoverageItem('group', {
        cvg,
        cvgPriceData: priceData.groupCoverages.find((cvgPrice) => cvg.starGroupId === cvgPrice.id),
        selectedGuestUnit,
      }),
    );
  }, [planAndCoverages, priceData, selectedPlanCoverages, selectedGuestUnit]);

  const coverageTableItems = useMemo(() => {
    return [
      ...optionalCoverageTableItems.filter(
        (cvg) => !coverageHandler.isSellersCoverageByCvgId(cvg.coverageId),
      ),
      ...groupCoverageTableItems.filter(
        (cvg) => !coverageHandler.isSellersCoverageByGroupId(cvg.groupId),
      ),
      ...oldProductGuestUnitTableItems,
    ]; //.sort(sortCoverageItems);
  }, [optionalCoverageTableItems, groupCoverageTableItems, oldProductGuestUnitTableItems]);

  const coverageDetails = useMemo(() => {
    if (!coverageTableItems) return [];

    return coverageTableItems.map((c) => ({
      productCoverageID: c?.coverageId || '',
      promoted: false,
      price: c?.price || undefined,
      termSymbol: '',
    }));
  }, [coverageTableItems]);

  const handleMfeStateChange = useCallback(
    (newState) => {
      if (!newState) return;

      // Find the changed item(s)
      optionalCoverageTableItems.forEach((optCovTableItem) => {
        const existingQuantity =
          selectedPlanCoverages.optionalCoverages.find((optCov) => {
            return optCov.id === optCovTableItem.coverageId;
          })?.quantity || 0;

        const newQuantity =
          newState.find((optCov) => {
            return optCov.productCoverageID === optCovTableItem.coverageId;
          })?.quantity || 0;

        if (existingQuantity !== newQuantity) {
          if (optCovTableItem.quantityEnabled && (existingQuantity > 1 || newQuantity > 1)) {
            optCovTableItem.onQuantityChange(optCovTableItem.coverageId, newQuantity);
          } else {
            optCovTableItem.onSelect();
          }
        }
      });
    },
    [coverageTableItems, optionalCoverageTableItems],
  );

  const mfeState = useMemo(() => {
    return coverageDetails.map((coverage) => {
      const matchingCoverage = selectedPlanCoverages?.optionalCoverages.find(
        (selectedCoverage) => selectedCoverage.id === coverage.productCoverageID,
      );
      return {
        productCoverageID: coverage.productCoverageID,
        quantity: matchingCoverage ? matchingCoverage.quantity : 0,
      };
    });
  }, [coverageDetails, selectedPlanCoverages]);

  // if real-time brand changing ever becomes a feature, this would need brand as a dep
  useEffect(() => {
    loadZestyCoverageMeta();
  }, []);

  const onErrorMFE = useCallback((e) => {
    // console.error(e);
  }, []);

  useEffect(() => {
    if (mfeState) {
      if (mfeState.length > 0) {
        setIsLoadingInitialSelection(false);
      }
    }
  }, [mfeState]);

  return (
    <>
      {selectedPlanCoverages?.product && (
        <div className="border-t-1 border-gray-300 mt-5 py-3 space-y-2">
          <Panel
            rounded={IsTheme(Theme.Ahs2024) ? 'xl' : 'none'}
            backgroundColor="white"
            borderT={IsTheme(Theme.Ahs2024) ? '0' : '2'}
            borderColor="gray-100"
            shadow={false}
            className="flex flex-col product-add-on-selection-list-micro-frontend px-0 md:p-4"
            id="plans-optional-coverages"
          >
            <REText variant="heading-04" className="mt-5 mb-4" color="primary">
              {PLANS_AND_PRICES_LABELS.COVERAGE_SELECTION_HEADER}
            </REText>
            {!isLoadingInitialSelection && coverageTableItems?.length > 0 && (
              <ProductAddOnSelectionListMicroFrontend
                cmsBaseURL={zestyUrl}
                coverageDetailsList={coverageDetails}
                language="en"
                onError={onErrorMFE}
                productPlanID={planAndCoverages?.product?.starProductPlanID || ''}
                showQuantitySelector={true}
                onStateChange={handleMfeStateChange}
                onLoadIndicator={onMFELoadIndicator}
                state={mfeState}
                tenant={getCurrentThemeConfig().plansPricesMFETenant as Tenant}
                backgroundColor={getCurrentThemeConfig().plansPricesMFEBackgroundColor}
              />
            )}
          </Panel>
        </div>
      )}
    </>
  );
};

export default CoverageAddonsMFE;
